import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  approveBudgetCenter,
  discardBudgetCenterDraft,
  fetchBudgetCenter,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';
import Page from '@src/components/layout/Page';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const ALLOCATION_MODE_OPTIONS = [
  {
    value: 'optimize_freely',
    label: 'Optimize Freely',
    helper: 'Recommended so we can shift spend toward best performance.',
  },
  {
    value: 'set_preferences',
    label: 'Set Preferences',
    helper: 'Preferences are guidance only and are not strict constraints.',
  },
  {
    value: 'fixed_allocations',
    label: 'Fixed Allocations',
    helper: 'Use only when segment allocations must remain fixed.',
  },
];

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(', ');
}

function getCurrentMonthStart() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

function formatMonthLabel(value) {
  if (!value) {
    return 'Current Month';
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function emptySegment(period) {
  return {
    budget_id: null,
    source_budget_id: null,
    vertical: '',
    location: '',
    channel: '',
    amount: '',
    notes: '',
    adjustments: [],
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
  };
}

function normalizeSegment(segment, period) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segment?.vertical ?? segment?.vertical_data?.id ?? '',
    location: segment?.location ?? segment?.location_data?.id ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    notes: segment?.notes ?? '',
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    pendingAdjustment: {
      amount_delta: '',
      effective_date: period,
      notes: '',
    },
    vertical_label: segment?.vertical_data?.vertical || '',
    location_label: segment?.location_data?.name || '',
    channel_label: segment?.channel_data?.name || '',
  };
}

function segmentSignature(segment) {
  return [
    segment.vertical || '',
    segment.location || '',
    segment.channel || '',
    String(segment.amount || ''),
    String(segment.notes || ''),
  ].join('|');
}

function buildSegmentLabel(segment, verticalOptions, locationOptions, channelOptions) {
  const verticalLabel = verticalOptions.find((option) => String(option.value) === String(segment.vertical || ''))?.label;
  const locationLabel = locationOptions.find((option) => String(option.value) === String(segment.location || ''))?.label;
  const channelLabel = channelOptions.find((option) => String(option.value) === String(segment.channel || ''))?.label;
  return [verticalLabel, locationLabel, channelLabel].filter(Boolean).join(' / ') || 'Segment';
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          <Typography color="text.secondary" variant="body2">
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function BudgetCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client_id') || '');
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [period, setPeriod] = useState(searchParams.get('period') || getCurrentMonthStart());
  const [pageData, setPageData] = useState(null);
  const [segments, setSegments] = useState([emptySegment(period)]);
  const [allocationMode, setAllocationMode] = useState('optimize_freely');
  const [allocationNotes, setAllocationNotes] = useState('');
  const [allocationConfig, setAllocationConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete();

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const canShowClientSelector = isStaff || clientOptions.length > 1;

  useEffect(() => {
    api.request({ url: 'dashboard/verticals/', method: 'GET' })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setVerticalOptions(rows.map((row) => ({ value: row.id, label: row.vertical })));
      })
      .catch(() => {});

    api.request({ url: 'dashboard/channels/', method: 'GET' })
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
        setChannelOptions(rows.map((row) => ({ value: row.id, label: row.name })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClient) {
      const option = findOptionByValue(selectedClient);
      if (option) {
        setSelectedClientOption(option);
      }
      api.request({
        url: 'dashboard/client-territories/',
        method: 'GET',
        payload: { client_id: selectedClient },
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setLocationOptions(rows.map((row) => ({ value: row.id, label: row.name })));
        })
        .catch(() => setLocationOptions([]));
      return;
    }

    const fallbackClientId = searchParams.get('client_id')
      || memberships.find((membership) => membership.client_id)?.client_id
      || (clientOptions.length === 1 ? clientOptions[0].value : '');
    if (fallbackClientId) {
      setSelectedClient(String(fallbackClientId));
    }
  }, [clientOptions, findOptionByValue, memberships, searchParams, selectedClient]);

  const loadBudgetCenter = useCallback(() => {
    if (!selectedClient) {
      return;
    }
    setLoading(true);
    fetchBudgetCenter({ client_id: selectedClient, period })
      .then((response) => {
        const payload = response.data;
        setPageData(payload);
        const nextSegments = Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(payload?.period || period)];
        setSegments(nextSegments);
        setAllocationMode(payload?.allocation_mode || payload?.current_budget?.allocation_mode || 'optimize_freely');
        setAllocationConfig(payload?.allocation_config || payload?.current_budget?.allocation_config || {});
        setAllocationNotes(payload?.notes || payload?.current_budget?.notes || '');
      })
      .catch((error) => {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to load Budget Center.') });
      })
      .finally(() => setLoading(false));
  }, [period, selectedClient]);

  useEffect(() => {
    loadBudgetCenter();
  }, [loadBudgetCenter]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (selectedClient) {
      nextParams.set('client_id', String(selectedClient));
    } else {
      nextParams.delete('client_id');
    }
    if (period) {
      nextParams.set('period', period);
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [period, searchParams, selectedClient, setSearchParams]);

  const currentSummary = pageData?.current_budget || null;
  const previousSummary = pageData?.previous_budget || null;
  const totalSegmentBudget = useMemo(
    () => segments.reduce((sum, segment) => sum + (Number(segment.amount) || 0), 0),
    [segments]
  );
  const selectedModeMeta = ALLOCATION_MODE_OPTIONS.find((option) => option.value === allocationMode);

  const updateSegment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, segmentIndex) => (
      segmentIndex === index ? { ...segment, [field]: value } : segment
    )));
  };

  const updatePendingAdjustment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, segmentIndex) => (
      segmentIndex === index
        ? {
          ...segment,
          pendingAdjustment: {
            ...(segment.pendingAdjustment || {}),
            [field]: value,
          },
        }
        : segment
    )));
  };

  const buildSavePayload = useCallback(() => {
    const normalizedSegments = segments.map((segment) => ({
      budget_id: segment.budget_id || null,
      source_budget_id: segment.source_budget_id || null,
      vertical: segment.vertical || null,
      vertical_ids: segment.vertical ? [Number(segment.vertical)] : [],
      location: segment.location || null,
      location_ids: segment.location ? [Number(segment.location)] : [],
      channel: segment.channel || null,
      amount: segment.amount,
      notes: segment.notes || '',
    }));

    let nextAllocationConfig = allocationConfig;
    if (allocationMode === 'fixed_allocations') {
      nextAllocationConfig = {
        items: normalizedSegments.map((segment, index) => ({
          label: buildSegmentLabel(segments[index], verticalOptions, locationOptions, channelOptions),
          amount: String(segment.amount || ''),
        })),
      };
    }

    return {
      client_id: Number(selectedClient),
      period,
      allocation_mode: allocationMode,
      allocation_config: nextAllocationConfig,
      notes: allocationNotes,
      segments: normalizedSegments,
    };
  }, [
    allocationConfig,
    allocationMode,
    allocationNotes,
    channelOptions,
    locationOptions,
    period,
    segments,
    selectedClient,
    verticalOptions,
  ]);

  const runBudgetAction = useCallback(async (requestFn, successMessage) => {
    if (!selectedClient) {
      setBanner({ severity: 'error', message: 'Select a client first.' });
      return null;
    }
    setSaving(true);
    try {
      const response = await requestFn(buildSavePayload());
      setPageData(response.data);
      setSegments(
        Array.isArray(response.data?.segments) && response.data.segments.length
          ? response.data.segments.map((segment) => normalizeSegment(segment, response.data.period || period))
          : [emptySegment(period)]
      );
      setAllocationMode(response.data?.allocation_mode || allocationMode);
      setAllocationConfig(response.data?.allocation_config || {});
      setAllocationNotes(response.data?.notes || allocationNotes);
      setBanner({ severity: 'success', message: successMessage });
      return response.data;
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Budget action failed.') });
      return null;
    } finally {
      setSaving(false);
    }
  }, [allocationMode, allocationNotes, buildSavePayload, period, selectedClient]);

  const saveDraft = useCallback(() => runBudgetAction(saveBudgetCenterDraft, 'Draft saved.'), [runBudgetAction]);
  const approveDraft = useCallback(() => runBudgetAction(approveBudgetCenter, 'Budget set approved.'), [runBudgetAction]);

  const discardDraft = useCallback(async () => {
    if (!selectedClient) {
      return;
    }
    setSaving(true);
    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(selectedClient),
        period,
      });
      setPageData(response.data);
      setSegments(
        Array.isArray(response.data?.segments) && response.data.segments.length
          ? response.data.segments.map((segment) => normalizeSegment(segment, response.data.period || period))
          : [emptySegment(period)]
      );
      setAllocationMode(response.data?.allocation_mode || 'optimize_freely');
      setAllocationConfig(response.data?.allocation_config || {});
      setAllocationNotes(response.data?.notes || '');
      setBanner({ severity: 'success', message: 'Draft discarded.' });
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to discard draft.') });
    } finally {
      setSaving(false);
    }
  }, [period, selectedClient]);

  const ensureDraftSegment = useCallback(async (segment) => {
    if (pageData?.has_draft && segment.budget_id && pageData?.draft_budget_ids?.includes(segment.budget_id)) {
      return segment.budget_id;
    }
    const signature = segmentSignature(segment);
    const responseData = await saveDraft();
    if (!responseData) {
      return null;
    }
    const matchingSegment = (responseData.segments || []).find(
      (row) => segmentSignature(normalizeSegment(row, responseData.period || period)) === signature
    );
    return matchingSegment?.budget_id || null;
  }, [pageData, period, saveDraft]);

  const addAdjustment = useCallback(async (index) => {
    const segment = segments[index];
    const pendingAdjustment = segment?.pendingAdjustment || {};
    if (!pendingAdjustment.amount_delta || !pendingAdjustment.effective_date) {
      setBanner({ severity: 'error', message: 'Adjustment amount and effective date are required.' });
      return;
    }
    const draftBudgetId = await ensureDraftSegment(segment);
    if (!draftBudgetId) {
      return;
    }

    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${draftBudgetId}/adjustments/`,
        method: 'POST',
        payload: pendingAdjustment,
      });
      setBanner({ severity: 'success', message: 'Adjustment added to draft segment.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to add adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [ensureDraftSegment, loadBudgetCenter, segments]);

  const deleteAdjustment = useCallback(async (segment, adjustmentId) => {
    const budgetId = segment?.budget_id;
    if (!budgetId || !pageData?.draft_budget_ids?.includes(budgetId)) {
      setBanner({ severity: 'info', message: 'Save a draft first to remove adjustments.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${budgetId}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      });
      setBanner({ severity: 'success', message: 'Adjustment removed.' });
      loadBudgetCenter();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to remove adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [loadBudgetCenter, pageData]);

  return (
    <Page title="Budget Center" fullWidth>
      <Stack spacing={3}>
        <Box>
          <Typography variant="body1">
            Manage monthly budget intent as a full segment set. Baseline budgets stay separate from in-month adjustments, and nothing goes live until the draft month is approved.
          </Typography>
        </Box>

        {banner ? <Alert severity={banner.severity}>{banner.message}</Alert> : null}

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {canShowClientSelector ? (
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    loading={clientLoading}
                    onChange={(_, option) => {
                      setSelectedClient(option?.value ? String(option.value) : '');
                      setSelectedClientOption(option || null);
                    }}
                    onInputChange={handleClientSearch}
                    options={clientOptions}
                    value={selectedClientOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderInput={(params) => <TextField {...params} label="Client" placeholder="Select client" />}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled
                    fullWidth
                    label="Client"
                    value={pageData?.client?.name || memberships.find((membership) => membership.client_id)?.client_name || ''}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Billing Period"
                  type="date"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  disabled
                  fullWidth
                  label="Workflow Status"
                  value={String(pageData?.status || 'draft').replaceAll('_', ' ')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && selectedClient ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label={`${formatMonthLabel(period)} Baseline`}
                  value={formatCurrency(currentSummary?.amount || totalSegmentBudget)}
                  helper={`${currentSummary?.segment_count || segments.length} budget segments in the current working set.`}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label={`${formatMonthLabel(pageData?.previous_period)} Budget`}
                  value={formatCurrency(previousSummary?.effective_amount || previousSummary?.amount)}
                  helper={`${previousSummary?.segment_count || 0} segments in the prior month.`}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label="Current Adjustment Total"
                  value={formatCurrency(currentSummary?.adjustment_total)}
                  helper="Net in-month increases and cuts across all current-month segments."
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SummaryCard
                  label="Current Effective Budget"
                  value={formatCurrency(currentSummary?.effective_amount || currentSummary?.amount)}
                  helper={selectedModeMeta?.helper || 'Recommended to keep flexibility high.'}
                />
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">Month Controls</Typography>
                    <Typography color="text.secondary" variant="body2">
                      Set the overall operating mode for the month, then manage the individual segment rows below.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        select
                        label="Allocation Mode"
                        value={allocationMode}
                        onChange={(event) => setAllocationMode(event.target.value)}
                      >
                        {ALLOCATION_MODE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Month Notes"
                        value={allocationNotes}
                        onChange={(event) => setAllocationNotes(event.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Alert severity={allocationMode === 'fixed_allocations' ? 'warning' : 'info'}>
                    {selectedModeMeta?.helper}
                  </Alert>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="h6">Budget Segments</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Clients with multiple markets, verticals, or channels can manage the whole monthly segment set here.
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => setSegments((prev) => [...prev, emptySegment(period)])}
                    >
                      Add Segment
                    </Button>
                  </Stack>

                  {segments.map((segment, index) => (
                    <Card key={`${segment.budget_id || 'new'}-${index}`} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TuneIcon fontSize="small" />
                              <Typography variant="subtitle1">Segment {index + 1}</Typography>
                              {segment.budget_id ? (
                                <Chip
                                  label={pageData?.draft_budget_ids?.includes(segment.budget_id) ? 'Draft' : 'Live'}
                                  size="small"
                                  color={pageData?.draft_budget_ids?.includes(segment.budget_id) ? 'warning' : 'success'}
                                />
                              ) : (
                                <Chip label="New" size="small" variant="outlined" />
                              )}
                            </Stack>
                            <IconButton
                              onClick={() => setSegments((prev) => prev.filter((_, segmentIndex) => segmentIndex !== index))}
                              disabled={segments.length === 1}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                select
                                label="Vertical"
                                value={segment.vertical}
                                onChange={(event) => updateSegment(index, 'vertical', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {verticalOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                select
                                label="Market / Location"
                                value={segment.location}
                                onChange={(event) => updateSegment(index, 'location', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {locationOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                select
                                label="Channel"
                                value={segment.channel}
                                onChange={(event) => updateSegment(index, 'channel', event.target.value)}
                              >
                                <MenuItem value="">None</MenuItem>
                                {channelOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label="Baseline Amount"
                                type="number"
                                value={segment.amount}
                                onChange={(event) => updateSegment(index, 'amount', event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Segment Notes"
                                value={segment.notes}
                                onChange={(event) => updateSegment(index, 'notes', event.target.value)}
                              />
                            </Grid>
                          </Grid>

                          <Divider />

                          <Stack spacing={1.5}>
                            <Typography variant="subtitle2">In-Month Adjustments</Typography>
                            {segment.adjustments?.length ? segment.adjustments.map((adjustment) => (
                              <Box key={adjustment.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                                  <Box>
                                    <Typography variant="body1">{formatCurrency(adjustment.amount_delta)}</Typography>
                                    <Typography color="text.secondary" variant="body2">
                                      Effective {dayjs(adjustment.effective_date).format('MMM D, YYYY')}
                                    </Typography>
                                    {adjustment.notes ? (
                                      <Typography color="text.secondary" variant="body2">
                                        {adjustment.notes}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  <Button
                                    color="inherit"
                                    onClick={() => deleteAdjustment(segment, adjustment.id)}
                                    variant="text"
                                  >
                                    Remove
                                  </Button>
                                </Stack>
                              </Box>
                            )) : (
                              <Typography color="text.secondary" variant="body2">
                                No adjustments on this segment yet.
                              </Typography>
                            )}

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="Adjustment Amount"
                                  type="number"
                                  value={segment.pendingAdjustment?.amount_delta || ''}
                                  onChange={(event) => updatePendingAdjustment(index, 'amount_delta', event.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label="Effective Date"
                                  type="date"
                                  value={segment.pendingAdjustment?.effective_date || period}
                                  onChange={(event) => updatePendingAdjustment(index, 'effective_date', event.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  label="Adjustment Notes"
                                  value={segment.pendingAdjustment?.notes || ''}
                                  onChange={(event) => updatePendingAdjustment(index, 'notes', event.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <Button
                                  fullWidth
                                  sx={{ height: '100%' }}
                                  variant="outlined"
                                  onClick={() => addAdjustment(index)}
                                >
                                  Add
                                </Button>
                              </Grid>
                            </Grid>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                  <Stack spacing={0.5}>
                    <Typography variant="h6">Actions</Typography>
                    <Typography color="text.secondary" variant="body2">
                      Draft first, then approve the full segment set when ready.
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      disabled={saving}
                      onClick={saveDraft}
                      startIcon={<SaveOutlinedIcon />}
                      variant="contained"
                    >
                      Save Draft
                    </Button>
                    <Button
                      disabled={saving}
                      onClick={approveDraft}
                      startIcon={<CheckCircleOutlineIcon />}
                      variant="outlined"
                    >
                      Approve / Submit
                    </Button>
                    <Button
                      disabled={saving || !pageData?.has_draft}
                      onClick={discardDraft}
                      startIcon={<UndoOutlinedIcon />}
                      variant="text"
                    >
                      Discard Changes
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">History</Typography>
                    <Typography color="text.secondary" variant="body2">
                      Existing activity log entries are reused so changes still show who changed what and when.
                    </Typography>
                  </Box>
                  {(pageData?.history || []).length ? pageData.history.map((entry) => (
                    <Box key={entry.id} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, py: 0.5 }}>
                      <Typography variant="subtitle2">{entry.summary}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {dayjs(entry.created_at).format('MMM D, YYYY h:mm A')}
                        {entry.actor?.name ? ` by ${entry.actor.name}` : ''}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography color="text.secondary" variant="body2">
                      No budget activity logged for this period yet.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Stack>
    </Page>
  );
}
